import { memo } from 'react';
import { 
  GraduationCap, 
  Users, 
  HelpCircle, 
  Tag, 
  MessageCircle,
  Shield,
  Brain,
  Zap,
  TrendingUp 
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useScrollAnimation, useStaggeredAnimation } from '@/hooks/useScrollAnimation';

const ValueSection = memo(() => {
  const headerReveal = useScrollAnimation();
  const benefitsReveal = useScrollAnimation();
  const differentialReveal = useScrollAnimation();
  const statsReveal = useScrollAnimation();
  const benefits = [
    {
      icon: GraduationCap,
      title: "AutoTrading Academy",
      description: "Acesso à nossa área de alunos com aulas detalhadas sobre configuração dos nossos robôs",
      highlight: "Aulas Exclusivas",
      color: "from-cyan-400 to-blue-500"
    },
    {
      icon: Users,
      title: "Sala ao Vivo",
      description: "Segunda a sexta das 08:30h às 12h com acompanhamento em tempo real das operações automatizadas",
      highlight: "Ao Vivo",
      color: "from-green-400 to-emerald-500"
    },
    {
      icon: HelpCircle,
      title: "Suporte Educacional",
      description: "Tira dúvidas em tempo real durante as salas ao vivo com especialistas experientes",
      highlight: "Suporte Especializado",
      color: "from-purple-400 to-violet-500"
    },
    {
      icon: Tag,
      title: "Descontos Exclusivos",
      description: "Acesso a descontos especiais em outros produtos e programas da plataforma",
      highlight: "Ofertas VIP",
      color: "from-orange-400 to-red-500"
    },
    {
      icon: MessageCircle,
      title: "Comunidade Connect",
      description: "Acesso à comunidade exclusiva de traders para compartilhar estratégias, resultados e trocar experiências com membros experientes",
      highlight: "Networking Premium",
      color: "from-pink-400 to-rose-500"
    },
    {
      icon: TrendingUp,
      title: "Templates de Configuração",
      description: "Templates de configurações padrão para facilitar sua configuração e adaptação ao ativo pretendido",
      highlight: "Pronto para Usar",
      color: "from-indigo-400 to-blue-500"
    }
  ];

  return (
    <section id="value" className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div 
          ref={headerReveal.ref}
          className={`text-center mb-16 transition-all duration-700 ${
            headerReveal.isVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="inline-flex items-center bg-gradient-to-r from-cyan-400/10 to-green-400/10 rounded-full px-6 py-2 mb-6">
            <span className="text-cyan-400 font-semibold">✨ VALOR EXCLUSIVO</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            O que você <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-green-400">REALMENTE LEVA</span>
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Muito mais que robôs de trading. Uma experiência completa de aprendizado, 
            suporte e comunidade para acelerar seus resultados.
          </p>
        </div>

        {/* Benefits Grid */}
        <div 
          ref={benefitsReveal.ref}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16"
        >
          {benefits.map((benefit, index) => (
            <Card 
              key={index} 
              className={`bg-slate-800/50 border-slate-700/50 hover:border-slate-600/50 transition-all duration-500 hover:transform hover:scale-105 group scroll-animate stagger-${index + 1} ${benefitsReveal.isVisible ? 'visible' : ''}`}
            >
              <CardContent className="p-8">
                <div className="relative mb-6">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${benefit.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <benefit.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r ${benefit.color} shadow-lg`}>
                      {benefit.highlight}
                    </span>
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors duration-300">
                  {benefit.title}
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  {benefit.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Value Proposition */}
        <div 
          ref={differentialReveal.ref}
          className={`bg-gradient-to-r from-slate-800/80 to-slate-700/80 rounded-3xl p-8 md:p-12 border border-slate-600/30 transition-all duration-700 ${
            differentialReveal.isVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Content */}
            <div>
              <div className="text-center lg:text-left mb-6">
                <div className="inline-flex items-center bg-gradient-to-r from-green-400/10 to-cyan-400/10 rounded-full px-4 py-2">
                  <span className="text-green-400 font-semibold">🎯 DIFERENCIAL COMPETITIVO</span>
                </div>
              </div>
              
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Não vendemos apenas <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-green-400">robôs</span>
              </h3>
              
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                Oferecemos uma <strong className="text-white">experiência completa de transformação</strong> no trading automatizado, 
                com educação premium, suporte especializado e uma comunidade ativa de traders de sucesso.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-gradient-to-r from-cyan-400 to-green-400 rounded-full"></div>
                  <span className="text-slate-300">Mais de <strong className="text-white">17 horas semanais</strong> de conteúdo ao vivo</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-gradient-to-r from-cyan-400 to-green-400 rounded-full"></div>
                  <span className="text-slate-300">Acompanhamento <strong className="text-white">personalizado</strong> nas salas ao vivo</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-gradient-to-r from-cyan-400 to-green-400 rounded-full"></div>
                  <span className="text-slate-300">Comunidade com <strong className="text-white">traders ativos</strong></span>
                </div>
              </div>
            </div>
            
            {/* Right Stats */}
            <div 
              ref={statsReveal.ref}
              className="grid grid-cols-2 gap-6"
            >
              {[
                { icon: Shield, text1: "Validação", text2: "Profissional", color: "text-green-400" },
                { icon: Brain, text1: "Zero", text2: "Emoção", color: "text-cyan-400" },
                { icon: Zap, text1: "Mais", text2: "Liberdade", color: "text-purple-400" },
                { icon: TrendingUp, text1: "Gestão de", text2: "Risco", color: "text-yellow-400" }
              ].map((item, index) => (
                <div 
                  key={index}
                  className={`text-center p-6 bg-slate-900/50 rounded-2xl border border-slate-600/30 transition-all duration-500 scroll-animate stagger-${index + 1} ${statsReveal.isVisible ? 'visible' : ''}`}
                >
                  <item.icon className={`mx-auto mb-3 h-8 w-8 ${item.color}`} />
                  <div className={`text-sm font-bold ${item.color}`}>{item.text1}</div>
                  <div className={`text-sm font-bold ${item.color}`}>{item.text2}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

ValueSection.displayName = 'ValueSection';

export default ValueSection;